import { gql, useMutation } from "@apollo/client";
import { Field, Heading, HStack, Input, NumberInput, Text, Textarea, VStack } from "@chakra-ui/react";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";
import * as v from "valibot";
import type {
  TemplateEditForm_TemplateFragment,
  TemplateEditForm_UpdateTemplateMutation,
  TemplateEditForm_UpdateTemplateMutationVariables,
} from "../graphql/generated/apollo";
import { Button } from "./ui/button";
import { toaster } from "./ui/toaster";

gql`
  fragment TemplateEditForm_Template on templates {
    id
    name
    is_default
    shop_id
    data
  }
`;

const templateSchema = v.object({
  name: v.pipe(
    v.string(),
    v.minLength(1, "テンプレート名を入力してください"),
    v.maxLength(100, "テンプレート名は100文字以内で入力してください"),
  ),
  data: v.object({
    item: v.object({
      name: v.string(),
      description: v.string(),
      price: v.pipe(v.number(), v.minValue(0, "価格は0円以上で入力してください")),
    }),
  }),
});

type TemplateFormData = v.InferOutput<typeof templateSchema>;

type TemplateEditFormProps = {
  template: TemplateEditForm_TemplateFragment;
  onSave: () => void;
  onCancel: () => void;
};

export function TemplateEditForm({ template, onSave, onCancel }: TemplateEditFormProps) {
  const [updateTemplate, { loading }] = useMutation<
    TemplateEditForm_UpdateTemplateMutation,
    TemplateEditForm_UpdateTemplateMutationVariables
  >(gql`
    mutation TemplateEditForm_UpdateTemplate($id: uuid!, $input: templates_set_input!) {
      update_templates_by_pk(pk_columns: { id: $id }, _set: $input) {
        id
        name
        is_default
        shop_id
        data
      }
    }
  `);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TemplateFormData>({
    resolver: valibotResolver(templateSchema),
    defaultValues: {
      name: template.name,
      data: template.data,
    },
  });

  const onSubmit = async (data: TemplateFormData) => {
    try {
      await updateTemplate({
        variables: {
          id: template.id,
          input: {
            name: data.name,
            data: data.data,
          },
        },
      });
      toaster.success({
        title: "テンプレートを更新しました",
      });
      onSave();
    } catch (error) {
      console.error("Failed to update template:", error);
      toaster.error({
        title: "テンプレートの更新に失敗しました",
      });
    }
  };

  return (
    <>
      <Field.Root>
        <Field.Label>テンプレート名</Field.Label>
        <Input {...register("name")} placeholder="テンプレート名を入力" />
        {errors.name && (
          <Text color="#f23a3c" textAlign="left" fontSize="small">
            {errors.name.message}
          </Text>
        )}
      </Field.Root>
      <Heading fontSize="md" textAlign="left" mt={4}>
        商品情報
      </Heading>
      <Heading fontSize="sm" textAlign="left" mt={2}>
        基本情報
      </Heading>
      <VStack gap={4} align="stretch" mt={2}>
        <Field.Root>
          <Field.Label>商品名</Field.Label>
          <Input {...register("data.item.name")} placeholder="商品名" />
          {errors.data?.item?.name && (
            <Text color="red" textAlign="left" fontSize="small">
              {errors.data?.item.name.message}
            </Text>
          )}
        </Field.Root>
        <Field.Root>
          <Field.Label>説明</Field.Label>
          <Textarea {...register("data.item.description")} placeholder="説明" rows={4} />
          {errors.data?.item?.description && (
            <Text color="red" textAlign="left" fontSize="small">
              {errors.data?.item.description.message}
            </Text>
          )}
        </Field.Root>
        <Field.Root>
          <Field.Label>価格</Field.Label>
          <NumberInput.Root w="full">
            <NumberInput.Input {...register("data.item.price", { valueAsNumber: true })} placeholder="価格" />
          </NumberInput.Root>
          {errors.data?.item?.price && (
            <Text color="red" textAlign="left" fontSize="small">
              {errors.data?.item.price.message}
            </Text>
          )}
        </Field.Root>
      </VStack>

      <HStack justify="flex-end" gap={4} mt={4}>
        <Button variant="outline" onClick={onCancel}>
          キャンセル
        </Button>
        <Button onClick={handleSubmit(onSubmit)} loading={loading}>
          保存
        </Button>
      </HStack>
    </>
  );
}
